// Funções puras extraídas de WorkoutAdminModal.jsx (issue 012, docs/07-issues.md)
// — sem estado, sem JSX, sem hooks. Movidas pra cá pra reduzir o tamanho do
// arquivo principal (6356 linhas antes desta extração); comportamento
// idêntico, só mudou de arquivo.

// Mesmo criterio de filtro+ordenacao usado pelos useMemo visiblePlans/
// listablePlans (ver mais abaixo no componente): treinos proprios do
// professor primeiro, splits padrao da plataforma depois, alfabetico dentro
// de cada grupo, sem o "em branco" pra quem nao e admin. Extraido em funcao
// pura pra poder ser chamado tambem em refreshAll() logo apos o fetch, antes
// do estado (plans/currentUser) assentar — sem isso, a selecao inicial usava
// a ordem crua da API (plansData[0]) e nao batia com o 1o item mostrado na
// lista da esquerda.
export function computeListablePlans(plansList, user) {
const list = Array.isArray(plansList) ? plansList : [];
const isAdminUser = user?.role === "admin";
const isProfessorUser = user?.role === "professor";
const visible = isAdminUser
  ? list
  : isProfessorUser
  ? list.filter((p) => p.professor_id === user?.id || p.is_blank || p.is_standard)
  : [];
const sorted = [...visible].sort((a, b) => {
  const standardDiff = (a.is_standard ? 1 : 0) - (b.is_standard ? 1 : 0);
  if (standardDiff !== 0) return standardDiff;
  return (a.title || "").localeCompare(b.title || "", "pt-BR", { sensitivity: "base" });
});
return isAdminUser ? sorted : sorted.filter((p) => !p.is_blank);
}

export function getEmptyExerciseItemForm() {
return {
  name: "",
  muscle_group: "",
  equipment: "",
  movement_type: "",
  demo_url: "",
  notes: "",
  is_active: true,
};
}

export function getEmptyPlanForm() {
return {
  title: "",
  description: "",
  is_active: true,
  is_blank: false,
};
}

export function getEmptyPlanItemForm() {
return {
  exercise_id: "",
  exercise_item_id: "",
  order_index: 0,
  sets_planned: "",
  reps_planned: "",
  reps_per_set: [],
  duration_planned: "",
  weight_planned: "",
  rest_seconds: "",
  rest_per_set: [],
  notes: "",
};
}

export function getEmptyPresetForm() {
return {
  name: "",
  sets_planned: "",
  reps_planned: "",
  reps_per_set: [],
  rest_seconds: "",
  rest_per_set: [],
  notes: "",
};
}

export function parseRepsPerSet(repsStr, n) {
  const parts = (repsStr || "").split(",").map(s => s.trim()).filter(Boolean);
  return Array.from({ length: n }, (_, i) => parts[i] ?? parts[0] ?? "");
}
export function formatRepsPerSet(arr) { return arr.join(","); }
// Alias para descanso por série (mesma lógica)
export const parseRestPerSet = parseRepsPerSet;
export const formatRestPerSet = formatRepsPerSet;

// Bi-set / superset / circuito: transforma a lista plana de itens (já
// ordenada por order_index) numa lista de "blocos" pra renderização —
// cada bloco é um item avulso OU um grupo contíguo de itens com o mesmo
// group_id. O backend garante que itens de um mesmo grupo ficam
// contíguos (ver /items/group em workout_plans.py), então um simples
// scan sequencial basta pra reconstruir os grupos aqui.
export function buildItemBlocks(items) {
  const blocks = [];
  let currentGroup = null;
  for (const item of items || []) {
    if (item.group_id != null && currentGroup && currentGroup.groupId === item.group_id) {
      currentGroup.items.push(item);
    } else {
      if (item.group_id != null) {
        currentGroup = { kind: "group", groupId: item.group_id, items: [item] };
        blocks.push(currentGroup);
      } else {
        currentGroup = null;
        blocks.push({ kind: "item", item });
      }
    }
  }
  return blocks;
}

// Rótulo do grupo — não existe campo separado "tipo do grupo" (ver
// docstring de group_id no model), é derivado dos próprios itens:
//   - 3+ exercícios → Circuito (geralmente full body, foco em resistência)
//   - 2 exercícios do MESMO exercise_id/categoria (mesmo grupo muscular,
//     ex. supino + crucifixo, ambos peito) → Bi-set (intensidade máxima)
//   - 2 exercícios de categorias DIFERENTES (ex. rosca direta + tríceps
//     testa, bíceps x tríceps) → Superset (ganho de tempo/rendimento)
// A distinção Bi-set vs Superset NÃO é sobre quantidade (as duas são
// sempre 2 exercícios) — é sobre o grupo muscular ser o mesmo ou não.
// `exercise_id` é o campo certo pra comparar: é a FK pra `Exercise`, que É
// a categoria/grupo muscular já exibida como badge em cada item
// (`item.category?.name`) — ver app/models/exercise.py.
export function groupLabel(items) {
  const size = items?.length ?? 0;
  if (size >= 3) return "Circuito";
  if (size === 2) {
    const [a, b] = items;
    const sameMuscleGroup = a?.exercise_id != null && a.exercise_id === b?.exercise_id;
    return sameMuscleGroup ? "Bi-set" : "Superset";
  }
  return "Bi-set"; // fallback defensivo — grupo de 1 item não deveria existir
}

// Detecta quando a seleção atual (groupSelectedIds) vai DESFAZER um grupo já
// existente — acontece quando um item selecionado já tem group_id, mas nem
// todo mundo daquele grupo está selecionado junto. O backend (POST
// .../items/group) não sabe disso: ele simplesmente move o item selecionado
// pro grupo novo, e se o grupo antigo cair pra <2 itens, _cleanup_lonely_groups
// desfaz ele silenciosamente. Sem esse aviso o professor só descobre ao
// reabrir a lista (achado #1 da auditoria UX — ver conversa). Funciona tanto
// pra WorkoutPlanItem (template) quanto UserWorkoutPlanItem (vínculo do
// aluno), já que ambos têm id + group_id + exercise_item no mesmo formato.
export function findGroupConflicts(items, selectedIds) {
  const selectedSet = new Set(selectedIds);
  const byGroup = {};
  (items || []).forEach((it) => {
    if (it.group_id == null) return;
    if (!byGroup[it.group_id]) byGroup[it.group_id] = [];
    byGroup[it.group_id].push(it);
  });
  const conflicts = [];
  Object.entries(byGroup).forEach(([gid, members]) => {
    const selectedMembers = members.filter((m) => selectedSet.has(m.id));
    if (selectedMembers.length > 0 && selectedMembers.length < members.length) {
      conflicts.push({
        groupId: Number(gid),
        names: members.map((m) => m.exercise_item?.name || `Item ${m.id}`),
      });
    }
  });
  return conflicts;
}

// Achado #1 da revisão UX (06/09/2026, ver GroupSetsTracker.jsx): se os
// itens selecionados pra agrupar têm sets_planned DIFERENTES, o exercício
// com menos séries sai do rodízio antes dos outros durante a execução —
// comportamento que o dado permite mas não é o uso recomendado (bi-set/
// superset normalmente tem o mesmo número de séries nos dois lados). Sem
// aviso aqui, o professor só descobre quando o aluno já está no meio do
// treino e um exercício "some" sem explicação. Retorna null se não há
// divergência (ou não dá pra comparar — menos de 2 itens com sets_planned
// definido). Mesmo padrão de findGroupConflicts: usado tanto pro template
// quanto pro vínculo do aluno.
export function findSetsMismatch(items, selectedIds) {
  const selectedSet = new Set(selectedIds);
  const selected = (items || []).filter((it) => selectedSet.has(it.id));
  const withSets = selected.filter((it) => it.sets_planned != null);
  if (withSets.length < 2) return null;
  const distinct = new Set(withSets.map((it) => Number(it.sets_planned)));
  if (distinct.size < 2) return null;
  return selected.map((it) => ({
    name: it.exercise_item?.name || `Item ${it.id}`,
    sets: it.sets_planned ?? "?",
  }));
}

// Espelha o limite de tamanho do backend pra imagem/gif (12MB — ver
// app/api/exercise_items.py, professor_exercise_media.py e
// student_exercise_media.py) pra avisar ANTES de subir o arquivo inteiro.
// Achado de revisão: sem isso, o professor/aluno só descobre que passou do
// limite depois de esperar o upload inteiro terminar e receber um 413.
//
// Vídeo NÃO tem mais teto aqui: o backend aceita o bruto de qualquer
// tamanho e comprime em segundo plano (app/jobs/video_compression_job.py)
// — decisão explícita pra não travar o aluno que grava um .mov grande
// direto no iPhone. Só o RESULTADO comprimido continua limitado a 50MB,
// o que é responsabilidade do job, não do client.
export const MAX_MEDIA_IMAGE_MB = 12;

export function checkMediaFileSize(file) {
  if (!file) return null;
  const ext = (file.name?.split(".").pop() || "").toLowerCase();
  const type = file.type || "";
  const isVideo = type.startsWith("video/") || ["mp4", "webm", "mov"].includes(ext);
  if (isVideo) return null;
  if (file.size > MAX_MEDIA_IMAGE_MB * 1024 * 1024) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return `Arquivo muito grande (${sizeMB}MB). Limite: ${MAX_MEDIA_IMAGE_MB}MB para imagem.`;
  }
  return null;
}

export function getOptionLabel(options, value) {
return options.find((opt) => opt.value === value)?.label || value || "";
}

export const AI_GENERATING_MESSAGES = [
  "Analisando o perfil do aluno…",
  "Selecionando os exercícios ideais…",
  "Montando a divisão semanal…",
  "Ajustando volumes e cargas…",
  "Personalizando pela rotina do aluno…",
  "Finalizando a ficha…",
];

// Camada de z-index pros modais de IA, que ficam "empilhados sobre modal":
// o de geração (AIGenerateModal) pode ser aberto de dentro de outro modal
// já aberto (ex.: StudentAssignmentModal, que usa .innerOverlay, z-index
// 10001 — WorkoutAdminModal.module.css), e o de confirmação (AIConfirmModal)
// abre por cima do PRÓPRIO modal de geração. Valores bem acima de 10001
// de propósito (defensivo, não é um "degrau" pensado da escala) — achado
// centralizado aqui em vez de espalhado como literal mágico em 2 arquivos
// diferentes (revisão de fragilidades pós-refactor, issue 012), sem mudar
// o valor numérico em si (zero risco visual, sem like como testar sem
// navegador nesta sessão). Se algum dia precisar reordenar de verdade,
// mexer nos dois juntos — a ordem relativa entre eles importa.
export const Z_AI_GENERATE_OVERLAY = 9999999;
export const Z_AI_CONFIRM_OVERLAY = 10000000; // sempre acima de Z_AI_GENERATE_OVERLAY
